import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Bed, Bath, MapPin, Heart, Eye, Share2, ChevronLeft, ChevronRight } from 'lucide-react';
import { readListingStreetViewState } from '../lib/listingCache';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function listingStreetViewUrl(property) {
  if (!property?.id || !property?.listing_street_view_image) return null;
  return `${API_URL}/uploads/listing-images/${property.id}/${property.listing_street_view_image}`;
}

export const PropertyCard = ({
  property,
  onSave,
  isSaved = false,
  showActions = true,
  useOverlayActions = false,
  onShare,
}) => {
  const {
    id,
    title,
    address,
    postcode,
    rent,
    bedrooms,
    bathrooms,
    property_type,
    listing_images,
    images,
    source,
    status
  } = property;

  const displayStatus = property?.is_occupied ? 'occupied' : (status || 'available');

  const [cachedStreetView, setCachedStreetView] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    if (!id || source === 'openrent') {
      setCachedStreetView(null);
      return;
    }

    try {
      const storedStreetViewUrl = listingStreetViewUrl(property);
      if (storedStreetViewUrl) {
        setCachedStreetView(storedStreetViewUrl);
        return;
      }

      const streetViewState = readListingStreetViewState(id);
      if (streetViewState.error || streetViewState.hidden) {
        setCachedStreetView(null);
        return;
      }

      setCachedStreetView(streetViewState.dataUrl || null);
    } catch {
      setCachedStreetView(null);
    }
  }, [id, property, source]);

  useEffect(() => {
    setImageIndex(0);
  }, [id]);

  const detailSlides = source !== 'openrent'
    ? [
        ...(cachedStreetView ? [{ type: 'sv', src: cachedStreetView }] : []),
        ...((Array.isArray(listing_images) && listing_images.length > 0)
          ? listing_images.map((filename) => ({
              type: 'listing',
              src: `${API_URL}/uploads/listing-images/${id}/${filename}`,
            }))
          : (Array.isArray(images) ? images : []).map((src) => ({ type: 'fallback', src }))),
      ]
    : (Array.isArray(images) ? images : []).map((src) => ({ type: 'openrent', src }));

  const boundedImageIndex = Math.max(0, Math.min(imageIndex, Math.max(detailSlides.length - 1, 0)));

  const imageSrc = detailSlides[boundedImageIndex]?.src
    || (source === 'openrent'
      ? 'https://staticcdn.openrent.co.uk/images/NoImageImage4x3.png'
      : 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800');

  const locationLabel = address || 'Location not provided';

  const statusColors = {
    available: 'bg-green-100 text-green-700 border-green-200',
    occupied: 'bg-slate-100 text-slate-700 border-slate-200',
    maintenance: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  };

  return (
    <Card 
      className="group overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-1"
      data-testid={`property-card-${id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={imageSrc}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {detailSlides.length > 1 ? (
          <>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setImageIndex((current) => (current - 1 + detailSlides.length) % detailSlides.length);
              }}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-8 h-8 flex items-center justify-center"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setImageIndex((current) => (current + 1) % detailSlides.length);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-8 h-8 flex items-center justify-center"
              aria-label="Next image"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {detailSlides.map((_, index) => (
                <button
                  key={`${id}-slide-${index}`}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setImageIndex(index);
                  }}
                  className={`h-2 rounded-full transition-all ${index === boundedImageIndex ? 'w-5 bg-white' : 'w-2 bg-white/60 hover:bg-white/80'}`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          </>
        ) : null}
        <div className="absolute top-3 left-3 flex flex-col gap-1">
          {source === 'openrent' && (
            <span className="bg-gray-900 text-white text-xs font-semibold px-2 py-0.5 rounded self-start">
              External · OpenRent
            </span>
          )}
          <div className="flex gap-2">
            <Badge className={`${statusColors[displayStatus] || statusColors.available} capitalize`}>
              {displayStatus}
            </Badge>
            <Badge variant="secondary" className="capitalize">
              {property_type}
            </Badge>
          </div>
        </div>
        {useOverlayActions ? (
          <div className="absolute top-3 right-3 flex gap-2">
            {onSave ? (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  onSave(id);
                }}
                className={`p-2 rounded-full transition-colors flex items-center justify-center ${
                  isSaved
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-white/90 text-navy-600 hover:bg-red-50 hover:text-red-500'
                }`}
                title={isSaved ? 'Remove from saved' : 'Save listing'}
                aria-label={isSaved ? 'Remove from saved' : 'Save listing'}
              >
                <Heart className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
              </button>
            ) : null}
            {onShare ? (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  onShare(id);
                }}
                className="p-2 rounded-full transition-colors flex items-center justify-center bg-white/90 text-navy-600 hover:bg-slate-100"
                title="Share listing"
                aria-label="Share listing"
              >
                <Share2 className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        ) : showActions && onSave && (
          <button
            onClick={(e) => {
              e.preventDefault();
              onSave(id);
            }}
            className={`absolute top-3 right-3 p-2 rounded-full transition-colors flex items-center justify-center ${
              isSaved
                ? 'bg-red-500 text-white'
                : 'bg-white/90 text-navy-600 hover:bg-red-50 hover:text-red-500'
            }`}
            data-testid={`save-property-${id}`}
          >
            <Heart className={`h-4 w-4 ${isSaved ? 'fill-current' : ''}`} />
          </button>
        )}
      </div>

      <CardContent className="p-4">
        {/* Price */}
        <div className="flex items-baseline gap-1 mb-2">
          <span className="text-2xl font-heading font-semibold text-navy-900">
            £{rent?.toLocaleString()}
          </span>
          <span className="text-sm text-muted-foreground">/ month</span>
        </div>

        {/* Title */}
        <h3 className="font-heading font-medium text-lg text-navy-900 line-clamp-1 mb-2">
          {title}
        </h3>

        {/* Location */}
        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3">
          <MapPin className="h-4 w-4" />
          <span className="line-clamp-1">{locationLabel}</span>
        </div>

        {/* Features */}
        <div className="flex items-center gap-4 text-sm text-navy-600 mb-4">
          <div className="flex items-center gap-1">
            <Bed className="h-4 w-4" />
            <span>{bedrooms ?? '-'} {bedrooms === 1 ? 'Bed' : 'Beds'}</span>
          </div>
          <div className="flex items-center gap-1">
            <Bath className="h-4 w-4" />
            <span>{bathrooms ?? '-'} {bathrooms === 1 ? 'Bath' : 'Baths'}</span>
          </div>
        </div>

        {/* Actions */}
        {showActions && (
          <Button asChild className="w-full" data-testid={`view-property-${id}`}>
            <Link to={`/properties/${id}`}>
              <Eye className="h-4 w-4 mr-2" />
              View Details
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
